(() => {
  if (window.CellectDeviceManager) return;

  const storageKey = 'cellect.devices';
  const stateVersion = 2;
  const widgets = new Set();
  const productRequests = new Map();
  const swiperInstances = new WeakMap();
  let documentInitialized = false;
  let catalogPromise;
  let catalog;
  let state = readState();

  function readState() {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '{}');
      const devices = Array.isArray(saved.devices) ? [...new Set(saved.devices.filter(Boolean))] : [];
      const activeDevice = devices.includes(saved.activeDevice) ? saved.activeDevice : devices[0] || null;
      const activeDeviceLabel = activeDevice === saved.activeDevice && typeof saved.activeDeviceLabel === 'string'
        ? saved.activeDeviceLabel
        : null;
      return { version: stateVersion, activeDevice, activeDeviceLabel, devices };
    } catch (_) {
      return { version: stateVersion, activeDevice: null, activeDeviceLabel: null, devices: [] };
    }
  }

  function saveState(nextState) {
    state = {
      ...nextState,
      activeDeviceLabel: catalog?.devicesById.get(nextState.activeDevice)?.label || null
    };
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (_) {}
    renderAll();
  }

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function fetchCatalog(nextUrl) {
    const url = new URL(nextUrl || window.Shopify?.routes?.root || '/', window.location.origin);
    url.searchParams.set('section_id', 'c-device-catalog');

    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Device catalog request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const dataElement = documentFragment.querySelector('[data-device-catalog-response]');
        if (!dataElement) throw new Error('Device catalog response is missing.');

        const data = JSON.parse(dataElement.textContent);
        return data.nextUrl
          ? fetchCatalog(data.nextUrl).then((devices) => data.devices.concat(devices))
          : data.devices;
      });
  }

  function loadCatalog() {
    if (catalogPromise) return catalogPromise;

    catalogPromise = fetchCatalog()
      .then((data) => {
        //console.log("data: ", data);
        const devices = data
          .filter((device) => device?.id && device?.label)
          .map((device) => ({ ...device, search: normalize(device.label) }));
          devices
        const devicesById = new Map(devices.map((device) => [device.id, device]));
        catalog = { devices, devicesById };

        const validDevices = state.devices.filter((id) => devicesById.has(id));
        const activeDevice = validDevices.includes(state.activeDevice)
          ? state.activeDevice
          : validDevices[0] || null;
        const activeDeviceLabel = devicesById.get(activeDevice)?.label || null;

        if (validDevices.length !== state.devices.length || activeDevice !== state.activeDevice || activeDeviceLabel !== state.activeDeviceLabel) {
          state = { version: stateVersion, activeDevice, activeDeviceLabel, devices: validDevices };
          try {
            localStorage.setItem(storageKey, JSON.stringify(state));
          } catch (_) {}
        }

        return catalog;
      })
      .catch((error) => {
        catalogPromise = undefined;
        throw error;
      });

    return catalogPromise;
  }

  function activeDevice() {
    return catalog?.devicesById.get(state.activeDevice) || null;
  }

  function addDevice(id) {
    if (!catalog?.devicesById.has(id)) return;
    const devices = state.devices.includes(id) ? state.devices : [...state.devices, id];
    saveState({ version: stateVersion, activeDevice: id, devices });
  }

  function activateDevice(id) {
    if (!state.devices.includes(id)) return;
    saveState({ ...state, activeDevice: id });
  }

  function removeDevice(id) {
    const devices = state.devices.filter((deviceId) => deviceId !== id);
    const active = state.activeDevice === id ? devices[0] || null : state.activeDevice;
    saveState({ version: stateVersion, activeDevice: active, devices });
  }

  function chipMarkup(id) {
    const device = catalog?.devicesById.get(id);
    if (!device) return '';

    const chip = document.createElement('span');
    chip.className = `c-device-chip${id === state.activeDevice ? ' is-active' : ''}`;
    chip.dataset.deviceId = id;

    const activate = document.createElement('button');
    activate.type = 'button';
    activate.className = 'c-device-chip__activate';
    activate.dataset.deviceActivate = id;
    activate.textContent = device.label;
    activate.setAttribute('aria-pressed', String(id === state.activeDevice));
    activate.setAttribute('aria-label', `${device.label}${id === state.activeDevice ? ', aktív készülék' : ' aktiválása'}`);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'c-device-chip__remove';
    remove.dataset.deviceRemove = id;
    remove.textContent = '×';
    remove.setAttribute('aria-label', `${device.label} eltávolítása`);

    chip.append(activate, remove);
    return chip;
  }

  function renderChips(container) {
    container.replaceChildren(...state.devices.map(chipMarkup).filter(Boolean));
  }

  function closeResults(widget) {
    widget.results.hidden = true;
    widget.input.setAttribute('aria-expanded', 'false');
  }

  function renderResults(widget) {
    const query = normalize(widget.input.value);
    widget.pendingId = null;
    widget.add.disabled = true;

    if (!query) {
      widget.results.replaceChildren();
      closeResults(widget);
      return;
    }

    const matches = catalog.devices.filter((device) => device.search.includes(query)).slice(0, 12);
    const items = matches.map((device) => {
      const item = document.createElement('li');
      item.setAttribute('role', 'option');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'c-device-picker__result';
      button.dataset.deviceResult = device.id;
      button.textContent = device.label;
      item.appendChild(button);
      return item;
    });

    if (!items.length) {
      const empty = document.createElement('li');
      empty.className = 'c-device-picker__status';
      empty.textContent = 'Nincs találat.';
      items.push(empty);
    }

    widget.results.replaceChildren(...items);
    widget.results.hidden = false;
    widget.input.setAttribute('aria-expanded', 'true');
  }

  function renderWidget(widget) {
    widget.input.disabled = !catalog;
    renderChips(widget.selected);
    if (widget.status) {
      widget.status.hidden = true;
      widget.status.textContent = '';
    }
  }

  function initializeWidget(element) {
    if (element.dataset.devicePickerInitialized) return;

    const widget = {
      element,
      input: element.querySelector('[data-device-input]'),
      results: element.querySelector('[data-device-results]'),
      add: element.querySelector('[data-device-add]'),
      selected: element.querySelector('[data-device-selected]'),
      status: element.querySelector('[data-device-status]'),
      pendingId: null,
    };

    if (!widget.input || !widget.results || !widget.add || !widget.selected) return;
    element.dataset.devicePickerInitialized = 'true';
    widgets.add(widget);

    widget.input.addEventListener('input', () => renderResults(widget));
    widget.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeResults(widget);
    });

    widget.results.addEventListener('click', (event) => {
      const result = event.target.closest('[data-device-result]');
      if (!result) return;
      const device = catalog.devicesById.get(result.dataset.deviceResult);
      if (!device) return;

      widget.pendingId = device.id;
      widget.input.value = device.label;
      widget.add.disabled = false;
      closeResults(widget);
      widget.add.focus();
    });

    widget.add.addEventListener('click', () => {
      if (!widget.pendingId) return;
      addDevice(widget.pendingId);
      widget.element.closest('[data-phone-drawer]')?.querySelector('[data-phone-drawer-close]')?.click();
      widget.pendingId = null;
      widget.input.value = '';
      widget.add.disabled = true;
      closeResults(widget);
    });

    renderWidget(widget);
  }

  function searchUrl(query) {
    const url = new URL(`${window.Shopify?.routes?.root || '/'}search`, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    return url;
  }

  function deviceSearchUrl(query) {
    const url = searchUrl(query);
    url.searchParams.set('device_filter', '1');
    return url;
  }

  function recommendationUrl(query) {
    const url = searchUrl(query);
    url.searchParams.set('section_id', 'c-for-you-products');
    return url.href;
  }

  function loadProducts(query) {
    const url = recommendationUrl(query);
    if (productRequests.has(url)) return productRequests.get(url);

    const request = fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((response) => {
        if (!response.ok) throw new Error(`For-you products request failed: ${response.status}`);
        return response.text();
      })
      .catch((error) => {
        productRequests.delete(url);
        throw error;
      });

    productRequests.set(url, request);
    return request;
  }

  function destroySwiper(section) {
    swiperInstances.get(section)?.destroy(true, true);
    swiperInstances.delete(section);
  }

  function initializeSwiper(section, slideCount) {
    const swiper = section.querySelector('[data-for-you-swiper]');
    if (!swiper || typeof window.Swiper !== 'function') return;

    destroySwiper(section);
    section.querySelector('[data-for-you-pagination]').hidden = slideCount <= 1;
    section.querySelectorAll('[data-for-you-navigation]').forEach((button) => {
      button.hidden = slideCount <= 4;
    });

    swiperInstances.set(
      section,
      new window.Swiper(swiper, {
        slidesPerView: 2,
        spaceBetween: 16,
        loop: false,
        a11y: false,
        pagination: {
          el: section.querySelector('[data-for-you-pagination]'),
          clickable: true,
        },
        navigation: {
          nextEl: section.querySelector('[data-for-you-next]'),
          prevEl: section.querySelector('[data-for-you-prev]'),
        },
        breakpoints: {
          576: { slidesPerView: 2.2 },
          990: { slidesPerView: 'auto' },
        },
      })
    );
  }

  function updateForYouSection(section) {
    const loadingState = section.querySelector('[data-for-you-loading]');
    const emptyState = section.querySelector('[data-for-you-empty]');
    const activeState = section.querySelector('[data-for-you-active]');
    const wrapper = section.querySelector('[data-for-you-wrapper]');
    const status = section.querySelector('[data-for-you-products-status]');
    const active = activeDevice();

    section.querySelectorAll('[data-active-device-label]').forEach((element) => {
      element.textContent = active?.label || '';
    });

    if (!active) {
      section.dataset.activeDevice = '';
      loadingState.hidden = true;
      emptyState.hidden = false;
      activeState.hidden = true;
      destroySwiper(section);
      return;
    }

    section.querySelector('[data-for-you-all-results]').href = deviceSearchUrl(active.label).href;

    if (section.dataset.activeDevice === active.id && wrapper.children.length) {
      loadingState.hidden = true;
      emptyState.hidden = true;
      activeState.hidden = false;
      return;
    }

    section.dataset.activeDevice = active.id;
    loadingState.hidden = false;
    emptyState.hidden = true;
    activeState.hidden = true;

    loadProducts(active.label)
      .then((html) => {
        if (section.dataset.activeDevice !== active.id) return;
        const responseDocument = new DOMParser().parseFromString(html, 'text/html');
        const response = responseDocument.querySelector('[data-for-you-products-response]');
        const slides = response ? [...response.children] : [];

        wrapper.replaceChildren(...slides);
        loadingState.hidden = true;
        activeState.hidden = false;
        status.hidden = slides.length > 0;
        status.textContent = slides.length ? '' : 'Ehhez a készülékhez jelenleg nincs elérhető termék.';
        initializeSwiper(section, slides.length);
        window.CProductCardGallery?.scan?.();
        window.HulkappWishlist?.init?.();
        window.jdgm?.initializeWidgets?.();
      })
      .catch((error) => {
        if (section.dataset.activeDevice !== active.id) return;
        wrapper.replaceChildren();
        loadingState.hidden = true;
        activeState.hidden = false;
        status.hidden = false;
        status.textContent = 'Az ajánlások most nem tölthetők be. Kérjük, próbáld újra később.';
        destroySwiper(section);
        console.error('For-you recommendations failed.', error);
      });
  }

  function renderAll() {
    widgets.forEach((widget) => {
      if (!widget.element.isConnected) {
        widgets.delete(widget);
        return;
      }
      renderWidget(widget);
    });
    document.querySelectorAll('[data-device-chip-list]').forEach(renderChips);
    document.querySelectorAll('[data-for-you-section]').forEach(updateForYouSection);
  }

  function initializeCatalog() {
    loadCatalog()
      .then(renderAll)
      .catch((error) => {
        document.querySelectorAll('[data-for-you-section]').forEach((section) => {
          section.querySelector('[data-for-you-loading]').hidden = true;
          section.querySelector('[data-for-you-empty]').hidden = false;
          section.querySelector('[data-for-you-active]').hidden = true;
        });
        document.querySelectorAll('[data-device-status]').forEach((status) => {
          status.hidden = false;
          status.textContent = 'A készüléklista most nem tölthető be.';
        });
        console.error('Device catalog failed.', error);
      });
  }

  function initialize() {
    document.querySelectorAll('[data-device-picker]').forEach(initializeWidget);
    if (!state.activeDevice) {
      document.querySelectorAll('[data-for-you-section]').forEach((section) => {
        section.querySelector('[data-for-you-loading]').hidden = true;
        section.querySelector('[data-for-you-empty]').hidden = false;
      });
    }

    if (!documentInitialized) {
      documentInitialized = true;
      document.addEventListener('click', (event) => {
        if (event.target.closest('[data-phone-drawer-trigger], [data-open-device-drawer]')) initializeCatalog();

        const activate = event.target.closest('[data-device-activate]');
        if (activate) {
          activateDevice(activate.dataset.deviceActivate);
          return;
        }

        const remove = event.target.closest('[data-device-remove]');
        if (remove) {
          removeDevice(remove.dataset.deviceRemove);
          return;
        }

        if (event.target.closest('[data-open-device-drawer]')) {
          document.querySelector('[data-phone-drawer-trigger]')?.click();
          return;
        }

        const picker = event.target.closest('[data-device-picker]');
        widgets.forEach((widget) => {
          if (widget.element !== picker) closeResults(widget);
        });
      });
    }

    if (document.querySelector('[data-for-you-section]')) initializeCatalog();
  }

  window.addEventListener('storage', (event) => {
    if (event.key !== storageKey) return;
    state = readState();
    if (catalog) renderAll();
  });

  document.addEventListener('shopify:section:load', initialize);
  window.CellectDeviceManager = { initialize, getState: () => ({ ...state }) };
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', initialize, { once: true })
    : initialize();
})();
